import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button, Container, Typography, Paper } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useSystemAdmin } from '../../hooks/useSystemAdmin';

interface SystemAdminRouteProps {
  children: ReactNode;
}

/**
 * Protected route that only allows access to users from system-tier organizations
 * Used for /internal routes like onboarding-submissions
 */
export default function SystemAdminRoute({ children }: SystemAdminRouteProps) {
  const { isSystemAdmin, loading, error } = useSystemAdmin();

  // Show loading spinner while checking
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error if something went wrong
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error checking permissions: {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Container>
    );
  }

  // Show access denied if not system admin
  if (!isSystemAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LockIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            This page is only accessible to system administrators.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If you believe you should have access, please contact your system administrator.
          </Typography>
          <Button
            variant="contained"
            onClick={() => (window.location.href = '/')}
            sx={{ mt: 2 }}
          >
            Go Home
          </Button>
        </Paper>
      </Container>
    );
  }

  // User is system admin, render children
  return <>{children}</>;
}

